import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, ShoppingBag, Users, TrendingUp, Package, ArrowUpRight, ArrowDownRight, Zap, Target, PieChart, Activity, RefreshCw } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { SocketContext } from '../contexts/SocketContext';
import { OfflineContext } from '../contexts/OfflineContext';
import { useSettings } from '../contexts/SettingsContext';

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const socket = useContext(SocketContext);
  const { pendingCount, syncPendingSales, syncing: isSyncing } = useContext(OfflineContext);
  const { settings, currencySymbol } = useSettings();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, count: 0, breakdown: {} });
  const [invStats, setInvStats] = useState({ totalStockValue: 0, lowStockCount: 0 });
  const [weeklySales, setWeeklySales] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [margins, setMargins] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    try {
      const [daily, inv, weekly, top, low, marginData] = await Promise.all([
        api.get(`/reports/daily-sales?timezoneOffset=${new Date().getTimezoneOffset()}`),
        api.get('/inventory/summary'),
        api.get(`/reports/weekly-revenue?timezoneOffset=${new Date().getTimezoneOffset()}`),
        api.get('/reports/top-products'),
        api.get('/products/low-stock'),
        api.get('/reports/performance/margins')
      ]);

      setStats(daily.data);
      setInvStats(inv.data);
      setWeeklySales(weekly.data);
      setTopProducts(top.data);
      setLowStockProducts(low.data);
      setMargins(marginData.data);
      console.log('Dashboard Loaded Stats:', daily.data);
    } catch (err) {
      console.error('Failed to load dashboard stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (socket) {
      socket.on('new_sale', loadStats);
      socket.on('stock_update', loadStats);
      return () => {
        socket.off('new_sale');
        socket.off('stock_update');
      };
    }
  }, [socket]);


  const totalProfit = margins.reduce((acc, curr) => acc + curr.profit, 0);

  return (
    <div className="space-y-8 animate-fade-in relative pb-12">
      {/* Dynamic Background Blurs */}
      <div className="fixed top-20 right-20 w-96 h-96 bg-brand/10 rounded-full blur-[120px] animate-pulse-slow -z-10"></div>
      <div className="fixed bottom-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] animate-pulse-slow -z-10" style={{ animationDelay: '2s' }}></div>

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-theme-main tracking-tight uppercase">
            {settings.store_name ? (
              <>
                {settings.store_name.split(' ')[0]}
                <span className="text-brand">{settings.store_name.split(' ').slice(1).join(' ')}</span>
              </>
            ) : 'Dashboard'}
          </h1>
          <p className="text-theme-muted font-bold text-xs uppercase tracking-[0.2em] mt-1 opacity-70">
            Real-time Intelligence Engine
          </p>
        </div>
        <div className="flex items-center gap-3">
          {pendingCount > 0 && (
            <button 
              onClick={syncPendingSales}
              disabled={isSyncing}
              className="glass px-6 py-3 rounded-2xl flex items-center gap-3 bg-amber-500/10 border-amber-500/20 text-amber-500 animate-pulse hover:bg-amber-500/20 transition-all"
            >
              <RefreshCw size={18} className={isSyncing ? 'animate-spin' : ''} />
              <span className="text-xs font-black uppercase tracking-widest">{pendingCount} Sale{pendingCount > 1 ? 's' : ''} Pending Sync</span>
            </button>
          )}
          <div className="glass px-6 py-3 rounded-2xl flex items-center gap-4">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
            <span className="text-sm font-black text-theme-main uppercase tracking-widest">
              {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </div>
      </div>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard 
          title="Revenue Today" 
          value={`${currencySymbol}${stats.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
          icon={DollarSign} 
          trend="Live" 
          trendUp={true} 
          color="brand"
          description="Gross sales since midnight"
        />
        <KpiCard 
          title="Net Profit" 
          value={`${currencySymbol}${totalProfit.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} 
          icon={TrendingUp} 
          trend="Estimate" 
          trendUp={true} 
          color="purple"
          description="Revenue minus cost of goods"
        />
        <KpiCard 
          title="Daily Orders" 
          value={stats.count.toString()} 
          icon={ShoppingBag} 
          trend="Transactions" 
          trendUp={true} 
          color="blue"
          description="Orders processed today"
        />
        <KpiCard 
          title="Stock Health" 
          value={invStats.lowStockCount.toString()} 
          icon={Package} 
          trend={invStats.lowStockCount > 0 ? "Alert" : "Stable"} 
          trendUp={invStats.lowStockCount === 0} 
          color="rose"
          isAlert={invStats.lowStockCount > 0}
          description="Items needing restock"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Performance Chart */}
        <div className="lg:col-span-2 glass rounded-[2rem] p-8 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <Activity size={120} />
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 relative z-10">
            <div>
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Revenue Analytics</h3>
              <p className="text-xs font-bold text-theme-muted uppercase tracking-widest mt-1">7-Day Transaction Velocity</p>
            </div>
            <div className="flex gap-2">
              <button className="bg-brand text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:scale-105 transition-transform shadow-lg shadow-brand/20">
                Weekly
              </button>
              <button onClick={() => navigate('/app/reports')} className="glass px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors">
                Full Report
              </button>
            </div>
          </div>
          
          <div className="h-[350px] w-full relative z-10">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weeklySales} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="var(--brand)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis 
                  dataKey="name" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: 'var(--text-muted)', fontSize: 10, fontWeight: '900'}} 
                  dy={15}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{fill: 'var(--text-muted)', fontSize: 10, fontWeight: '900'}} 
                />
                <Tooltip 
                  cursor={{ stroke: 'var(--brand)', strokeWidth: 2, strokeDasharray: '5 5' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="glass-morphism p-4 rounded-2xl border border-white/10 shadow-2xl">
                          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-theme-muted mb-1">{payload[0].payload.name} Sales</p>
                          <p className="text-lg font-black text-theme-main">{currencySymbol}{payload[0].value.toLocaleString()}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="var(--brand)" 
                  strokeWidth={4} 
                  fillOpacity={1} 
                  fill="url(#colorSales)" 
                  animationDuration={2000}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Profit by Category */}
        <div className="glass rounded-[2rem] p-8 flex flex-col relative overflow-hidden">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-3 bg-purple-500/10 text-purple-500 rounded-2xl">
              <PieChart size={24} />
            </div>
            <div>
              <h3 className="text-lg font-black text-theme-main uppercase tracking-tight">Category Yield</h3>
              <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Performance by Profit</p>
            </div>
          </div>

          <div className="flex-1 space-y-6">
            {margins.length === 0 ? (
              <div className="h-full flex flex-center items-center justify-center italic text-theme-muted opacity-50">No data available</div>
            ) : margins.slice(0, 5).map((item, index) => (
              <div key={index} className="space-y-2">
                <div className="flex justify-between items-end">
                  <span className="text-xs font-black text-theme-main uppercase tracking-widest">{item.name}</span>
                  <span className="text-xs font-black text-brand">{item.margin}%</span>
                </div>
                <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5">
                  <div 
                    className="h-full bg-gradient-vibrant rounded-full transition-all duration-1000"
                    style={{ width: `${item.margin}%`, transitionDelay: `${index * 100}ms` }}
                  ></div>
                </div>
                <div className="flex justify-between text-[10px] font-bold text-theme-muted uppercase tracking-widest">
                  <span>Rev: {currencySymbol}{Math.round(item.revenue).toLocaleString()}</span>
                  <span>Profit: {currencySymbol}{Math.round(item.profit).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
          
          <button onClick={() => navigate('/app/inventory-insights')} className="mt-8 w-full glass py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-white/5 transition-all active:scale-95">
            Deep Insights
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Performers */}
        <div className="glass rounded-[2rem] p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-brand/10 text-brand rounded-2xl">
                <Target size={24} />
              </div>
              <h3 className="text-lg font-black text-theme-main uppercase tracking-tight">Elite Products</h3>
            </div>
            <Zap className="text-brand animate-pulse" size={20} />
          </div>

          <div className="grid grid-cols-1 gap-4">
            {topProducts.map((p, i) => (
              <div key={i} className="flex items-center justify-between p-5 bg-white/5 rounded-2xl border border-white/5 hover:border-brand/30 transition-all group cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm ${
                    i === 0 ? 'bg-amber-500/20 text-amber-500 glow-amber' :
                    i === 1 ? 'bg-slate-400/20 text-slate-400' :
                    i === 2 ? 'bg-orange-600/20 text-orange-600' : 'bg-white/5 text-theme-muted'
                  }`}>
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-black text-theme-main group-hover:text-brand transition-colors uppercase tracking-tight">{p.name}</p>
                    <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Best Seller</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-black text-theme-main">{p.sold}</p>
                  <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">Units Sold</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Inventory Pulse */}
        <div className="glass rounded-[2rem] p-8 relative overflow-hidden">
          <div className="flex items-center justify-between mb-8 relative z-10">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-rose-500/10 text-rose-500 rounded-2xl">
                <Activity size={24} />
              </div>
              <h3 className="text-lg font-black text-theme-main uppercase tracking-tight">Inventory Pulse</h3>
            </div>
            {lowStockProducts.length > 0 && (
              <span className="text-[10px] font-black bg-rose-500 text-white px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg shadow-rose-500/20">
                Alert Required
              </span>
            )}
          </div>

          <div className="space-y-4 relative z-10">
            {lowStockProducts.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-center">
                <div className="p-6 bg-emerald-500/10 text-emerald-500 rounded-full mb-4">
                  <Package size={48} />
                </div>
                <p className="font-black text-theme-main uppercase tracking-widest">Stock Balanced</p>
                <p className="text-xs font-bold text-theme-muted uppercase tracking-widest mt-1">All units are above threshold</p>
              </div>
            ) : lowStockProducts.slice(0, 4).map((p) => (
              <div key={p.id} className="p-5 bg-rose-500/5 rounded-2xl border border-rose-500/10 flex items-center justify-between group hover:bg-rose-500/10 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center font-black animate-pulse">
                    {p.stock}
                  </div>
                  <div>
                    <p className="font-black text-theme-main uppercase tracking-tight">{p.name}</p>
                    <p className="text-[10px] font-bold text-rose-500 uppercase tracking-widest">Critical Level</p>
                  </div>
                </div>
                <button onClick={() => navigate('/app/inventory')} className="p-3 text-theme-muted hover:text-rose-500 transition-colors">
                  <ArrowUpRight size={20} />
                </button>
              </div>
            ))}
          </div>
          
          <button onClick={() => navigate('/app/inventory')} className="mt-8 w-full glass py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-white/5 transition-all">
            Manage Stock
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, trend, trendUp, color, isAlert, description }) {
  const colorMap = {
    brand: 'from-emerald-500 to-emerald-600 shadow-emerald-500/20',
    purple: 'from-purple-500 to-indigo-600 shadow-purple-500/20',
    blue: 'from-blue-500 to-cyan-600 shadow-blue-500/20',
    rose: 'from-rose-500 to-pink-600 shadow-rose-500/20'
  };

  const iconColors = {
    brand: 'bg-emerald-500/10 text-emerald-500',
    purple: 'bg-purple-500/10 text-purple-500',
    blue: 'bg-blue-500/10 text-blue-500',
    rose: 'bg-rose-500/10 text-rose-500'
  };

  return (
    <div className={`glass rounded-[2rem] p-6 relative overflow-hidden group transition-all duration-500 hover:-translate-y-2 cursor-default ${isAlert ? 'border-rose-500/30' : 'hover:border-brand/30'}`}>
      <div className="flex justify-between items-start mb-6">
        <div className={`p-4 rounded-2xl ${iconColors[color]} transition-transform duration-500 group-hover:scale-110`}>
          <Icon size={28} />
        </div>
        <div className={`flex items-center gap-1.5 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest shadow-sm ${
          isAlert ? 'bg-rose-500 text-white glow-rose' :
          trendUp ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'
        }`}>
          {!isAlert && (trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />)}
          {trend}
        </div>
      </div>
      
      <div className="relative z-10">
        <h4 className="text-theme-muted text-[10px] font-black uppercase tracking-[0.2em] mb-2">{title}</h4>
        <div className="text-3xl font-black text-theme-main tracking-tight mb-2 group-hover:text-brand transition-colors">
          {value}
        </div>
        <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest opacity-60">
          {description}
        </p>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${colorMap[color]} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
    </div>
  );
}